import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, OnInit, OnDestroy, Inject, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Task, TaskStatus } from '@workspaces/models/task';
import { Workspace } from '@workspaces/models/workspaces';
import { taskListFeature, TaskListFetch } from '@workspaces/store/task-list.feature';
import { WorkspaceFetch, workspaceListFeature } from '@workspaces/store/workspace-list.feature';
import { EditComponent } from '@workspaces/utils/edit-component';
import { Subscription, interval, merge, fromEvent, switchMap, startWith } from 'rxjs';
import { AppState } from 'src/app/state/app.state';
import { WorkspaceStatus } from '../../models/agent';
import { workspaceAgentFeature, WorkspaceAgentFetch } from '@workspaces/store/agent-status.feature';
import { MatDialog } from '@angular/material/dialog';
import { WorkspaceStatusClassPipe } from '@workspaces/utils/pipes/workspace-status-class.pipe';
import { WorkspaceStatusPipe } from '@workspaces/utils/pipes/workspace-status.pipe';
import { AgentStatusPipe } from '@workspaces/utils/pipes/agent-status.pipe';
import { MatButtonModule } from '@angular/material/button';
import { WorkspaceService } from '@workspaces/services/workspace.service';
import { ConfirmationModalComponent } from 'src/app/confirmation-modal/confirmation-modal.component';
import { RequiredRoleDirective } from '@workspaces/utils/workspace-guard.directive';

@Component({
  selector: 'app-workspace-overview',
  templateUrl: './workspace-overview.component.html',
  styleUrls: ['./workspace-overview.component.scss', '../../global-workspace-styles.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatChipsModule,
    WorkspaceStatusPipe,
    WorkspaceStatusClassPipe,
    WorkspaceStatusPipe,
    AgentStatusPipe,
    MatButtonModule,
    RequiredRoleDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkspaceOverviewComponent extends EditComponent implements OnInit, OnDestroy {
  private readonly sub = new Subscription();
  public currentWorkspace: Workspace | null = null;
  public tasks: Task[] = [];
  public documentStatus: WorkspaceStatus = { status: 'STATUS_NOT_FOUND', workspace_id: '' };
  public archiveIsLoading = false;
  constructor(
    protected override readonly route: ActivatedRoute,
    protected override readonly snackbar: MatSnackBar,
    protected override readonly router: Router,
    private readonly store: Store<AppState>,
    @Inject(DOCUMENT) private readonly document: Document,
    public readonly dialog: MatDialog,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly workspaceService: WorkspaceService
  ) {
    super(route, snackbar, router);
  }

  public override ngOnInit() {
    super.ngOnInit();
    if (this.workspaceId) {
      this.dispatchFetches(this.workspaceId);
      this.setupPolling(this.workspaceId);
      this.sub.add(
        this.store.select(workspaceListFeature.workspaceById(this.workspaceId)).subscribe(workspace => {
          if (workspace && this.workspaceId === workspace.id) {
            this.currentWorkspace = workspace;
            this.changeDetectorRef.markForCheck(); // Dont copy the state, just mark for check to rerender
          }
        })
      );
      this.sub.add(
        this.store.select(taskListFeature.selectByWorkspaceId(this.workspaceId)).subscribe(tasks => {
          if (tasks) {
            this.tasks = tasks.filter(task =>
              (['todo', 'in_progress'] satisfies TaskStatus[] as TaskStatus[]).includes(task.status)
            );
            this.changeDetectorRef.markForCheck();
          }
        })
      );
      this.sub.add(
        this.store.select(workspaceAgentFeature.workspaceAgentData(this.workspaceId)).subscribe(status => {
          if (status && status.workspace_id === this.workspaceId) {
            this.documentStatus = status;
          }
        })
      );
    }
  }

  private dispatchFetches(workspaceId: string): void {
    this.store.dispatch(WorkspaceFetch({ workspaceId }));
    this.store.dispatch(TaskListFetch({ workspaceId }));
    this.store.dispatch(WorkspaceAgentFetch({ workspaceId }));
  }

  private setupPolling(workspaceId: string): void {
    const ONE_MINUTE_MS = 60000;
    const minutePoll$ = interval(ONE_MINUTE_MS);
    const windowFocus$ = fromEvent(this.document.defaultView!, 'focus');

    const refreshTrigger$ = merge(minutePoll$, windowFocus$).pipe(
      startWith(0),
      switchMap(() => {
        this.dispatchFetches(workspaceId);
        return [];
      })
    );

    this.sub.add(refreshTrigger$.subscribe());
  }

  public changeStatus(toArchive: boolean): void {
    if (!this.workspaceId || this.archiveIsLoading) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      maxWidth: '400px',
      data: {
        message: `Do you want to ${toArchive ? 'archive' : 'unarchive'} this workspace?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }
      this.archiveIsLoading = true;
      if (toArchive) {
        this.sub.add(
          this.workspaceService
            .archiveWorkspace(this.workspaceId)
            .subscribe(() => {
              this.dispatchFetches(this.workspaceId!);
              this.snackbar.open('Moved to archive.', 'Dismiss', {
                duration: 5000,
                panelClass: ['success-snackbar']
              });
            })
            .add(() => {
              this.archiveIsLoading = false;
            })
        );
      } else {
        this.sub.add(
          this.workspaceService
            .unarchiveWorkspace(this.workspaceId)
            .subscribe(() => {
              this.dispatchFetches(this.workspaceId!);
              this.snackbar.open('Moved to unarchive.', 'Dismiss', {
                duration: 5000,
                panelClass: ['success-snackbar']
              });
            })
            .add(() => {
              this.archiveIsLoading = false;
            })
        );
      }
    });
  }

  public ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
